import React, { memo } from 'react';
import { AppNode, NodeType } from '@/types';
import { useNodeStore } from '@/stores/nodeStore';
import { ConfirmPanel } from './ConfirmPanel';
import { FEATURE_GRIDS } from './GridOverlay';

const DEFAULT_ASPECT_RATIO = '16:9';

const FEATURE_PROMPTS: Record<string, string> = {
  '多机位九宫格': '基于一张参考图生成3×3不同机位/景别的关键帧，画面中不得出现任何文字、序号、编号、水印',
  '剧情推演四宫格': '基于一张参考图生成2×2时间线（生5s/3s）动态演变帧，画面中不得出现任何文字、序号、编号、水印',
  '25宫格连贯分镜': '基于一张图生成连贯的25宫格分镜（5*5），画面中不得出现任何文字、序号、编号、水印',
  '电影级光影校正': '一键仅调整光照/曝光/色温，不改变画面内容',
  '角色三视图生成': '一键生成角色三视图（正面/侧面/背面），画面中不得出现任何文字、序号、编号、水印',
  '画面推演 - 3秒后': '基于物理逻辑，生成3秒后的动作结果，画面中不得出现任何文字、序号、编号、水印',
  '画面推演 - 5秒前': '基于物理逻辑，反推5秒前的动作起因，画面中不得出现任何文字、序号、编号、水印',
};

interface FeatureConfirmBarProps {
  data: AppNode;
  cardHeight: number;
  activeFeature: { id: string; label: string };
  onNodeUpdate: (id: string, data: Partial<AppNode['data']>) => void;
  onNodeAction: (id: string, prompt?: string) => void;
  onAddNode?: (type: NodeType, position?: { x: number; y: number }, initialData?: Record<string, unknown>) => string | undefined;
  onClose: () => void;
}

export const FeatureConfirmBar: React.FC<FeatureConfirmBarProps> = memo(({
  data,
  cardHeight,
  activeFeature,
  onNodeUpdate,
  onNodeAction,
  onAddNode,
  onClose,
}) => {
  const handleConfirm = () => {
    if (!onAddNode) return;

    const autoPrompt = FEATURE_PROMPTS[activeFeature.label] || activeFeature.label;
    const selectedModel = data.data.model;
    const selectedResolution = data.data.resolution || '1k';

    const newX = data.x + 500;
    const newY = data.y;

    const sourceAspectRatio = data.data.aspectRatio || DEFAULT_ASPECT_RATIO;

    let targetAspectRatio = sourceAspectRatio;
    const gridConfig = FEATURE_GRIDS[activeFeature.label];
    if (gridConfig) {
      const [srcW, srcH] = sourceAspectRatio.split(':').map(Number);
      const overallW = gridConfig.cols * srcW;
      const overallH = gridConfig.rows * srcH;
      const calcGcd = (a: number, b: number): number => b === 0 ? a : calcGcd(b, a % b);
      const g = calcGcd(overallW, overallH);
      targetAspectRatio = `${overallW / g}:${overallH / g}`;
    }

    const newNodeId = onAddNode(NodeType.IMAGE_GENERATOR, { x: newX, y: newY }, {
      model: selectedModel,
      resolution: selectedResolution,
      prompt: autoPrompt,
      generationMode: 'IMG2IMG',
      aspectRatio: targetAspectRatio,
    });

    if (newNodeId) {
      useNodeStore.getState().addConnection(data.id, newNodeId);
      setTimeout(() => {
        onNodeAction(newNodeId, autoPrompt);
      }, 100);
    }

    onClose();
  };

  return (
    <ConfirmPanel
      data={data}
      cardHeight={cardHeight}
      title={activeFeature.label}
      modelFilter="img2img"
      onNodeUpdate={onNodeUpdate}
      onConfirm={handleConfirm}
      onClose={onClose}
    />
  );
});

FeatureConfirmBar.displayName = 'FeatureConfirmBar';
